import type { ReactNode } from "react";
import { format, isToday, isYesterday } from "date-fns";
import type { GetMessagesSource } from "@workspace/api-client-react";
import {
  type Message,
  type ListItem,
  type SortMode,
  type Folder,
  type TaskItem,
  type TaskTag,
  type TaskStatus,
  type GroupedEntry,
  type TaskViewFilters,
  PRIORITY_RANK,
  mergedStatuses,
} from "./types";

// A manually dragged position always wins (ascending, same convention as
// folder_prefs.sortOrder — explicitly-ordered items sort first, before
// anything computed). Priority is an arbitrary user-defined tag with no
// inherent rank, so absent a manual position the fallback is just
// soonest-due-first, then newest-created — the "Group by: Priority" control
// (not this comparator) is what priority-based organizing means now.
export function compareTasks(a: TaskItem, b: TaskItem): number {
  if (a.position !== null && b.position !== null) return a.position - b.position;
  if (a.position !== null) return -1;
  if (b.position !== null) return 1;
  const ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
  const bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
  if (ad !== bd) return ad - bd;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

// A saved view's multi-select filters round-trip through a Set (see
// TaskTablePane's currentFilters), so two states that select the exact same
// items can still differ in *array order* depending on click order — a
// naive JSON.stringify comparison would flag that as "changed" even though
// nothing meaningful did. Compares those fields as sets; everything else
// (scalars) is compared directly.
function sameSet(a: (string | number)[], b: (string | number)[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((v) => setB.has(v));
}

export function viewFiltersEqual(a: TaskViewFilters, b: TaskViewFilters): boolean {
  return (
    a.groupBy === b.groupBy &&
    a.hideDone === b.hideDone &&
    a.hideRecurring === b.hideRecurring &&
    a.nameFilter === b.nameFilter &&
    a.sortColumn === b.sortColumn &&
    a.sortDirection === b.sortDirection &&
    sameSet(a.listFilter, b.listFilter) &&
    sameSet(a.statusFilter, b.statusFilter) &&
    sameSet(a.priorityFilter, b.priorityFilter) &&
    sameSet(a.dueDateFilter, b.dueDateFilter)
  );
}

// Combines a date-only input value ("YYYY-MM-DD") with an optional
// time-of-day ("HH:MM") into a full timestamp, constructed from local date
// parts (not by parsing an ISO string) so the result lands at that literal
// local wall-clock time regardless of the browser's timezone offset from
// UTC. This is the fix for a real bug: the old code passed the date-only
// string straight to `new Date(...)`, which the JS spec parses as UTC
// midnight — for any timezone ahead of UTC (e.g. UK during BST), that
// instant is already in the past the moment the clock ticks past 1am local,
// so a task "due today" could show as overdue almost the entire day.
// Defaulting an unset time to 23:59 (not 00:00) also matches what a due
// date normally means with no time specified — due sometime that day, not
// due at its very first instant.
export function combineDueDateTime(dateStr: string, timeStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
  const [hh, mm] = (timeStr || "23:59").split(":").map(Number) as [number, number];
  return new Date(y, m - 1, d, hh, mm, 0, 0).toISOString();
}

// Local HH:MM for a stored due-date ISO string, for pre-filling the time
// input — read via Date's local getters, not sliced from the ISO string
// itself (which is always UTC), so it reflects the same wall-clock time the
// user actually sees/set.
export function dueTimeInputValue(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Local YYYY-MM-DD for a stored due-date ISO string, for pre-filling the
// date input. Same reasoning as dueTimeInputValue — `iso.slice(0, 10)` reads
// the UTC calendar date, which is a day off from the local one whenever the
// local time is on the other side of midnight from UTC (e.g. late evening
// in a negative-UTC-offset timezone rolls into the next UTC day).
export function dueDateInputValue(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Plain-text preview from a real HTML message body — for handing off to a
// plain-text destination (a task's description field, which uses this
// app's own markdown dialect, not real HTML) — a gmail/outlook message
// body is raw HTML, so a naive copy would carry visible tags along with
// the text. DOMParser rather than a regex: reliably handles nested tags,
// entities, and malformed markup the way a hand-rolled strip can't, and
// this only ever reads .textContent back out, never renders the parsed
// document, so there's no XSS surface despite parsing untrusted HTML.
export function stripHtmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
}

export function formatFolderLabel(folder: Folder): string {
  if (folder.isCustomLabel) return folder.label;
  if (folder.kind === "sent" || folder.kind === "list" || folder.kind === "provider_folder") return folder.label;
  if (folder.kind === "domain") {
    const name = folder.label.split(".")[0] || folder.label;
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
  if (folder.label.startsWith("DM: ")) return folder.label;
  if (folder.kind === "teams_conversation") return folder.label;
  return `#${folder.label}`;
}

// Remote (http/https) images in an email body are blocked by default — they
// can act as read-receipt/tracking pixels (the sender's server sees the
// request the instant the image loads, revealing you opened the email and
// often your IP). Inline cid: images are unaffected — those are already
// rewritten server-side (gmail.ts/outlook.ts) to point at our own
// same-origin attachment proxy, not a third-party server, so they're not a
// tracking vector and load normally. Only <img> src/srcset is handled here,
// not CSS background-image — the practical scope covers the overwhelming
// majority of real-world tracking pixels without the complexity of a full
// CSS-in-HTML rewrite.
export function blockRemoteImages(html: string): { html: string; hasRemoteImages: boolean } {
  if (!html) return { html, hasRemoteImages: false };
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let hasRemoteImages = false;
    doc.querySelectorAll("img[src]").forEach((img) => {
      const src = img.getAttribute("src") || "";
      if (!/^https?:\/\//i.test(src)) return;
      hasRemoteImages = true;
      img.setAttribute("data-blocked-src", src);
      img.removeAttribute("src");
      img.removeAttribute("srcset");
    });
    if (!hasRemoteImages) return { html, hasRemoteImages: false };
    // Hidden outright (not just src-less) so a blocked image doesn't leave
    // a broken-image glyph or an alt-text-sized gap — same end result as
    // every major client's own "images not displayed" treatment.
    const style = doc.createElement("style");
    style.textContent = "img[data-blocked-src] { display: none !important; }";
    doc.head.appendChild(style);
    return { html: doc.documentElement.outerHTML, hasRemoteImages: true };
  } catch {
    // Malformed HTML DOMParser can't handle — fail open to the original
    // body rather than showing a blank email; matches how the sandboxed
    // iframe already trusts the raw HTML today.
    return { html, hasRemoteImages: false };
  }
}

// Column-header click-to-sort in the "All Tasks" master view. Ascending
// order for each column; the caller negates the result for descending.
export function compareByColumn(a: TaskItem, b: TaskItem, column: string, tags: TaskTag[], folders: Folder[], customStatuses: TaskStatus[] = []): number {
  switch (column) {
    case "name":
      return a.title.localeCompare(b.title);
    case "list": {
      const labelFor = (t: TaskItem) => {
        const f = folders.find((x) => x.kind === "list" && Number(x.key.slice("list:".length)) === t.listId);
        return f ? formatFolderLabel(f) : "";
      };
      return labelFor(a).localeCompare(labelFor(b));
    }
    case "status": {
      // Built-ins first (their fixed workflow order), then this task's
      // custom statuses after — same "merge, don't replace" convention as
      // mergedStatuses() everywhere else custom statuses show up.
      const all = mergedStatuses(customStatuses);
      const ai = all.findIndex((s) => s.key === a.status);
      const bi = all.findIndex((s) => s.key === b.status);
      return ai - bi;
    }
    case "priority": {
      // Priority tags are free-form per-list (no severity field on the tag
      // itself — see task_tags schema), so there's no "high > medium > low"
      // to compute. Same convention as "Group by: Priority" (buildGroups in
      // TaskTable.tsx): the tags array's own order is the priority order —
      // whatever sequence the user created/arranged their levels in — not
      // alphabetical by name.
      const ai = tags.findIndex((t) => t.id === a.priorityTagId);
      const bi = tags.findIndex((t) => t.id === b.priorityTagId);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1; // no priority sorts last
      if (bi === -1) return -1;
      return ai - bi;
    }
    case "dueDate": {
      const ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      return ad - bd;
    }
    default:
      return 0;
  }
}

export function itemPinned(item: ListItem): boolean {
  return item.kind === "slack" || item.kind === "teams" || item.kind === "native" ? !!item.latest.isPinned : !!item.msg.isPinned;
}

export function itemTimestamp(item: ListItem): number {
  return new Date(item.kind === "slack" || item.kind === "teams" || item.kind === "native" ? item.latest.timestamp : item.msg.timestamp).getTime();
}

export function itemUnread(item: ListItem): boolean {
  return item.kind === "slack" || item.kind === "teams" || item.kind === "native" ? item.unreadCount > 0 : !item.msg.isRead;
}

export function itemPriorityRank(item: ListItem): number {
  const p = item.kind === "slack" || item.kind === "teams" || item.kind === "native" ? item.latest.aiPriority : item.msg.aiPriority;
  return p && p in PRIORITY_RANK ? PRIORITY_RANK[p as keyof typeof PRIORITY_RANK] : 3;
}

export function itemSnoozedUntil(item: ListItem): string | null | undefined {
  return item.kind === "slack" || item.kind === "teams" || item.kind === "native" ? item.latest.snoozedUntil : item.msg.snoozedUntil;
}

export function itemIsSnoozed(item: ListItem, now: number): boolean {
  const until = itemSnoozedUntil(item);
  return !!until && new Date(until).getTime() > now;
}

export function buildListItems(
  messages: Message[],
  filter: GetMessagesSource,
  sortMode: SortMode,
  showSnoozed: boolean,
  ignoreSnoozeFilter = false,
): ListItem[] {
  const items: ListItem[] = [];

  if (filter !== "slack" && filter !== "teams") {
    messages
      .filter((m) => m.source === "gmail")
      .forEach((msg) => items.push({ kind: "gmail", id: msg.id, msg }));
  }

  if (filter !== "gmail" && filter !== "slack" && filter !== "teams") {
    messages
      .filter((m) => m.source === "outlook")
      .forEach((msg) => items.push({ kind: "outlook", id: msg.id, msg }));
  }

  if (filter !== "gmail" && filter !== "outlook" && filter !== "teams") {
    const channelMap = new Map<string, Message[]>();
    messages
      .filter((m) => m.source === "slack")
      .forEach((msg) => {
        const ch = normalizeChannelName(msg.channel || "unknown");
        if (!channelMap.has(ch)) channelMap.set(ch, []);
        channelMap.get(ch)!.push(msg);
      });

    channelMap.forEach((msgs, channel) => {
      const sorted = [...msgs].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      items.push({
        kind: "slack",
        channel,
        messages: sorted,
        latest: sorted[0]!,
        unreadCount: msgs.filter((m) => !m.isRead).length,
      });
    });
  }

  if (filter !== "gmail" && filter !== "outlook" && filter !== "slack" && filter !== "teams") {
    const channelMap = new Map<string, Message[]>();
    messages
      .filter((m) => m.source === "native")
      .forEach((msg) => {
        const ch = msg.channel || "unknown";
        if (!channelMap.has(ch)) channelMap.set(ch, []);
        channelMap.get(ch)!.push(msg);
      });

    channelMap.forEach((msgs, channel) => {
      const sorted = [...msgs].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      items.push({
        kind: "native",
        channel,
        messages: sorted,
        latest: sorted[0]!,
        unreadCount: msgs.filter((m) => !m.isRead).length,
      });
    });
  }

  if (filter !== "gmail" && filter !== "outlook" && filter !== "slack") {
    const conversationMap = new Map<string, Message[]>();
    messages
      .filter((m) => m.source === "teams")
      .forEach((msg) => {
        // Teams has no single "channel name" to normalize the way Slack does
        // — group by whichever reply identifier is present (chat vs
        // team+channel), falling back to the display label if somehow
        // neither is set (shouldn't happen for a real Teams message).
        const key = msg.teamsChatId
          ? `chat:${msg.teamsChatId}`
          : msg.teamsTeamId && msg.teamsChannelId
            ? `channel:${msg.teamsTeamId}:${msg.teamsChannelId}`
            : `label:${msg.channel ?? "unknown"}`;
        if (!conversationMap.has(key)) conversationMap.set(key, []);
        conversationMap.get(key)!.push(msg);
      });

    conversationMap.forEach((msgs, key) => {
      const sorted = [...msgs].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      items.push({
        kind: "teams",
        channel: sorted[0]!.channel || "Teams",
        conversationKey: key,
        messages: sorted,
        latest: sorted[0]!,
        unreadCount: msgs.filter((m) => !m.isRead).length,
      });
    });
  }

  const now = Date.now();
  const visible = ignoreSnoozeFilter
    ? items
    : items.filter((item) => (showSnoozed ? itemIsSnoozed(item, now) : !itemIsSnoozed(item, now)));

  return visible.sort((a, b) => {
    // Pinned items always float to the top, regardless of sort mode.
    const pinnedDiff = Number(itemPinned(b)) - Number(itemPinned(a));
    if (pinnedDiff !== 0) return pinnedDiff;

    switch (sortMode) {
      case "oldest":
        return itemTimestamp(a) - itemTimestamp(b);
      case "unread": {
        const unreadDiff = Number(itemUnread(b)) - Number(itemUnread(a));
        return unreadDiff !== 0 ? unreadDiff : itemTimestamp(b) - itemTimestamp(a);
      }
      case "priority": {
        const rankDiff = itemPriorityRank(a) - itemPriorityRank(b);
        return rankDiff !== 0 ? rankDiff : itemTimestamp(b) - itemTimestamp(a);
      }
      case "newest":
      default:
        return itemTimestamp(b) - itemTimestamp(a);
    }
  });
}

// Chronological + grouped-by-day feed, used by both the Slack thread view and
// the Jira comment feed. Takes a newest-first array (as returned by the API).
export function groupMessagesByDay(messagesNewestFirst: Message[]): GroupedEntry[] {
  const chrono = [...messagesNewestFirst].reverse(); // oldest first
  const entries: GroupedEntry[] = [];

  for (let i = 0; i < chrono.length; i++) {
    const msg = chrono[i];
    const prev = chrono[i - 1];

    if (prev) {
      const d1 = new Date(prev.timestamp);
      const d2 = new Date(msg.timestamp);
      if (d1.toDateString() !== d2.toDateString()) {
        let label = format(d2, "MMM d, yyyy");
        if (isToday(d2)) label = "Today";
        else if (isYesterday(d2)) label = "Yesterday";
        entries.push({ kind: "day", label });
      }
    } else {
      const d = new Date(msg.timestamp);
      let label = format(d, "MMM d, yyyy");
      if (isToday(d)) label = "Today";
      else if (isYesterday(d)) label = "Yesterday";
      entries.push({ kind: "day", label });
    }

    const grouped = !!prev &&
      prev.fromName === msg.fromName &&
      new Date(msg.timestamp).getTime() - new Date(prev.timestamp).getTime() < 5 * 60_000;

    entries.push({ kind: "msg", msg, grouped });
  }

  return entries;
}

export function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export function slackKey(channel: string) {
  return `slack_channel:${normalizeChannelName(channel)}`;
}

export function normalizeChannelName(channel: string): string {
  return channel.replace(/^#\s*/, "");
}

// ─── Slack helpers ────────────────────────────────────────────────────────────

const SLACK_EMOJI_ALIASES: Record<string, string> = {
  grinning: "😀", joy: "😂", rofl: "🤣", blush: "😊", heart_eyes: "😍",
  smile: "😊", kissing_heart: "😘", sunglasses: "😎", thinking: "🤔",
  facepalm: "🤦", roll_eyes: "🙄", sweat_smile: "😅", sob: "😭",
  scream: "😱", exploding_head: "🤯", partying_face: "🥳", "+1": "👍",
  thumbsup: "👍", "-1": "👎", thumbsdown: "👎", wave: "👋", raised_hands: "🙌",
  clap: "👏", handshake: "🤝", pray: "🙏", muscle: "💪", v: "✌️",
  heart: "❤️", fire: "🔥", 100: "💯", white_check_mark: "✅", x: "❌",
  warning: "⚠️", star: "⭐", tada: "🎉", dart: "🎯", rocket: "🚀",
  bulb: "💡", email: "📧", briefcase: "💼", alarm_clock: "⏰",
  bell: "🔔", sparkles: "✨", zap: "⚡", star2: "🌟", trophy: "🏆",
  confetti_ball: "🎊", pinched_fingers: "🤌", speech_balloon: "💬",
  pushpin: "📌",
};

export function renderSlackText(text: string): string {
  return text.replace(/:([+\-\w]+):/g, (full, name: string) =>
    SLACK_EMOJI_ALIASES[name] ?? full
  );
}

export function renderMarkdown(text: string): ReactNode {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Escaped first, before any of the markdown substitutions below run —
    // `line` is a raw Slack/Teams message body from whoever sent it, and
    // every substitution below only ever emits a handful of fixed tags this
    // function controls. Escaping up front means arbitrary HTML/script a
    // sender wrote (e.g. an <img onerror=...>) can never survive into the
    // dangerouslySetInnerHTML calls further down — it renders as inert text.
    let html = line
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // italic
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');
    // strikethrough
    html = html.replace(/~~(.*?)~~/g, '<del>$1</del>');
    // code
    html = html.replace(/`(.*?)`/g, '<code class="bg-black/10 dark:bg-white/10 px-1 py-0.5 rounded text-[0.9em]">$1</code>');

    if (line.startsWith('> ')) {
      return <blockquote key={i} className="border-l-2 border-primary/50 pl-3 my-1 italic text-muted-foreground" dangerouslySetInnerHTML={{ __html: html.substring(2) }} />;
    }
    if (line.startsWith('• ') || line.startsWith('- ')) {
      return <div key={i} className="pl-4 relative"><span className="absolute left-0">•</span><span dangerouslySetInnerHTML={{ __html: html.substring(2) }} /></div>;
    }

    return <span key={i} dangerouslySetInnerHTML={{ __html: html }} className="block min-h-[1.2em]" />;
  });
}

const AVATAR_COLORS = [
  "bg-rose-500","bg-orange-500","bg-amber-500","bg-green-500",
  "bg-teal-500","bg-cyan-500","bg-blue-500","bg-violet-500","bg-pink-500",
];
export function avatarColor(name: string): string {
  const n = name.split("").reduce((s, c) => s + c.charCodeAt(0), 0);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}
